import { useState, useEffect } from 'react';
import {
  listPendingRestaurants,
  approveRestaurant,
  rejectRestaurant,
  type RestaurantApplication,
} from '@/shared/services/restaurantsService';
import { ConfirmModal } from '@/shared/components/ConfirmModal';
import { ImageCarousel } from '@/shared/components/ImageCarousel';
import styles from './PendingRestaurantsPage.module.css';

export function PendingRestaurantsPage() {
  const [applications, setApplications] = useState<RestaurantApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    applicationId: string | null;
    restaurantName: string;
    action: 'approve' | 'reject' | null;
  }>({
    isOpen: false,
    applicationId: null,
    restaurantName: '',
    action: null,
  });

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const apps = await listPendingRestaurants();
      setApplications(apps);
    } catch (err) {
      let errorMessage = 'Error al cargar solicitudes pendientes';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string }; status?: number } };
        if (axiosError.response?.status === 404) {
          errorMessage = 'El endpoint no está disponible. Verifica que el backend esté desplegado.';
        } else if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
          errorMessage = 'No tienes permisos para ver solicitudes pendientes.';
        } else {
          errorMessage = axiosError.response?.data?.message || axiosError.response?.data?.error || errorMessage;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenApproveModal = (application: RestaurantApplication) => {
    setModalState({
      isOpen: true,
      applicationId: application.applicationId,
      restaurantName: application.name,
      action: 'approve',
    });
  };

  const handleOpenRejectModal = (application: RestaurantApplication) => {
    setModalState({
      isOpen: true,
      applicationId: application.applicationId,
      restaurantName: application.name,
      action: 'reject',
    });
  };

  const handleCloseModal = () => {
    if (processingId) return; // No cerrar si está procesando
    setModalState({
      isOpen: false,
      applicationId: null,
      restaurantName: '',
      action: null,
    });
  };

  const handleConfirmAction = async () => {
    if (!modalState.applicationId || !modalState.action) return;

    try {
      setProcessingId(modalState.applicationId);
      setError('');
      setSuccessMessage('');

      if (modalState.action === 'approve') {
        await approveRestaurant(modalState.applicationId);
        setSuccessMessage('Restaurante aprobado exitosamente.');
      } else {
        await rejectRestaurant(modalState.applicationId);
        setSuccessMessage('Solicitud rechazada exitosamente.');
      }

      // Remover la solicitud de la lista
      setApplications((prev) =>
        prev.filter((app) => app.applicationId !== modalState.applicationId)
      );

      // Cerrar modal
      handleCloseModal();

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : modalState.action === 'approve'
            ? 'Error al aprobar el restaurante'
            : 'Error al rechazar la solicitud';
      setError(errorMessage);
    } finally {
      setProcessingId(null);
    }
  };

  const getModalContent = () => {
    if (!modalState.action) {
      return {
        title: '',
        message: '',
        variant: 'primary' as const,
        confirmText: 'Confirmar',
        showInput: false,
        inputLabel: '',
        inputPlaceholder: '',
        inputRequired: false,
      };
    }

    const restaurantName = modalState.restaurantName;

    if (modalState.action === 'approve') {
      return {
        title: '¿Aprobar restaurante?',
        message: `¿Estás seguro de que deseas aprobar la solicitud del restaurante "${restaurantName}"?\n\nAl aprobar:\n• Se creará el restaurante en el sistema\n• Se asignará el rol restaurant_admin al propietario\n• El restaurante podrá comenzar a operar`,
        variant: 'primary' as const,
        confirmText: 'Aprobar',
        showInput: false,
        inputLabel: '',
        inputPlaceholder: '',
        inputRequired: false,
      };
    }

    return {
      title: '¿Rechazar solicitud?',
      message: `¿Estás seguro de que deseas rechazar la solicitud del restaurante "${restaurantName}"?\n\nAl rechazar:\n• La solicitud será marcada como rechazada\n• El restaurante no será creado en el sistema\n• El propietario no recibirá acceso al dashboard`,
      variant: 'danger' as const,
      confirmText: 'Rechazar',
      showInput: true,
      inputLabel: 'Motivo del rechazo (opcional)',
      inputPlaceholder: 'Ej: Documentación incompleta, información incorrecta...',
      inputRequired: false,
    };
  };

  const formatDate = (timestamp?: { seconds: number; nanoseconds: number }) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Cargando solicitudes pendientes...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Solicitudes Pendientes</h1>
        <p className={styles.subtitle}>Gestiona las solicitudes de nuevos restaurantes</p>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {successMessage && <div className={styles.success}>{successMessage}</div>}

      {applications.length === 0 ? (
        <div className={styles.empty}>
          <p>No hay solicitudes pendientes en este momento.</p>
        </div>
      ) : (
        <div className={styles.applicationsList}>
          {applications.map((app) => (
            <div key={app.applicationId} className={styles.applicationCard}>
              <div className={styles.cardHeader}>
                <h3 className={styles.restaurantName}>{app.name}</h3>
                <span className={styles.statusBadge}>{app.status}</span>
              </div>

              <div className={styles.cardBody}>
                {app.images && app.images.length > 0 && (
                  <div className={styles.imagesSection}>
                    <span className={styles.label}>Imágenes del restaurante:</span>
                    <div className={styles.imagesContainer}>
                      <ImageCarousel images={app.images} maxImages={3} />
                    </div>
                  </div>
                )}
                <div className={styles.infoRow}>
                  <span className={styles.label}>Email:</span>
                  <span className={styles.value}>{app.email}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Teléfono:</span>
                  <span className={styles.value}>{app.phone}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Dirección:</span>
                  <span className={styles.value}>{app.addressText}</span>
                </div>
                {app.addressLat && app.addressLng && (
                  <div className={styles.infoRow}>
                    <span className={styles.label}>Coordenadas:</span>
                    <span className={styles.value}>
                      {app.addressLat.toFixed(6)}, {app.addressLng.toFixed(6)}
                    </span>
                  </div>
                )}
                <div className={styles.infoRow}>
                  <span className={styles.label}>Fecha de solicitud:</span>
                  <span className={styles.value}>{formatDate(app.createdAt)}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>ID de solicitud:</span>
                  <span className={styles.value}>{app.applicationId}</span>
                </div>
              </div>

              <div className={styles.cardActions}>
                <button
                  onClick={() => handleOpenApproveModal(app)}
                  disabled={processingId === app.applicationId}
                  className={`${styles.button} ${styles.approveButton}`}
                >
                  Aprobar
                </button>
                <button
                  onClick={() => handleOpenRejectModal(app)}
                  disabled={processingId === app.applicationId}
                  className={`${styles.button} ${styles.rejectButton}`}
                >
                  Rechazar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <p className={styles.count}>Total: {applications.length} solicitudes pendientes</p>
        <button onClick={loadApplications} className={styles.refreshButton} disabled={loading}>
          {loading ? 'Actualizando...' : 'Actualizar Lista'}
        </button>
      </div>

      {/* Modal de confirmación */}
      {modalState.isOpen && (
        <ConfirmModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmAction}
          title={getModalContent().title}
          message={getModalContent().message}
          confirmText={getModalContent().confirmText}
          cancelText="Cancelar"
          confirmButtonVariant={getModalContent().variant}
          isLoading={processingId === modalState.applicationId}
          showInput={getModalContent().showInput || false}
          inputLabel={getModalContent().inputLabel}
          inputPlaceholder={getModalContent().inputPlaceholder}
          inputRequired={getModalContent().inputRequired || false}
        />
      )}
    </div>
  );
}
